import type { LineString, MultiLineString, MultiPolygon, Point, Polygon, Position } from "geojson";

/**
 * Pure geometry helpers for street-parcel feature computation.
 *
 * These mirror the PostGIS definitions the model was trained against:
 * - lengths/areas use the same spherical/planar conventions noted per function;
 * - the oriented envelope is a *planar* minimum-area rectangle over the raw lon/lat
 *   coordinates (matching `ST_OrientedEnvelope`), not a geographically-rotated one;
 * - the centroid is the planar area centroid (matching `ST_Centroid`).
 */

/** Mean Earth radius used by PostGIS `ST_DistanceSphere` and Turf. */
const EARTH_RADIUS_M = 6371008.8;
const DEG_TO_RAD = Math.PI / 180;

/** Rings of every polygon part: a `Polygon` yields one entry, a `MultiPolygon` yields many. */
export const polygonParts = (geom: Polygon | MultiPolygon): Position[][][] => (geom.type === "MultiPolygon" ? geom.coordinates : [geom.coordinates]);

/** Great-circle distance between two `[lon, lat]` positions, in metres. */
export function haversineMetres(a: Position, b: Position): number {
  const dLat = (b[1] - a[1]) * DEG_TO_RAD;
  const dLon = (b[0] - a[0]) * DEG_TO_RAD;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * DEG_TO_RAD) * Math.cos(b[1] * DEG_TO_RAD) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/** Sum of great-circle edge lengths over every ring — matches `ST_Perimeter(geom::geography)`. */
export function geodesicPerimeterMetres(geom: Polygon | MultiPolygon): number {
  let perimeter = 0;
  for (const rings of polygonParts(geom))
    for (const ring of rings) for (let i = 0; i + 1 < ring.length; i++) perimeter += haversineMetres(ring[i], ring[i + 1]);
  return perimeter;
}

/** Total vertex count across all rings — matches `ST_NPoints`. */
export const pointCount = (geom: Polygon | MultiPolygon): number =>
  polygonParts(geom).reduce((total, rings) => total + rings.reduce((sum, ring) => sum + ring.length, 0), 0);

/** Number of polygon parts — matches `ST_NumGeometries`. */
export const partCount = (geom: Polygon | MultiPolygon): number => (geom.type === "MultiPolygon" ? geom.coordinates.length : 1);

/** Total interior rings (holes) across all parts. */
export const holeCount = (geom: Polygon | MultiPolygon): number => polygonParts(geom).reduce((total, rings) => total + rings.length - 1, 0);

const outerRingCoords = (geom: Polygon | MultiPolygon): Position[] => {
  const points: Position[] = [];
  for (const rings of polygonParts(geom)) for (const point of rings[0]) points.push(point);
  return points;
};

/** Planar convex hull (monotone chain) over lon/lat treated as cartesian. */
export function convexHull(points: Position[]): Position[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (sorted.length < 3) return sorted;
  const cross = (o: Position, a: Position, b: Position): number => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Position[] = [];
  for (const point of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) lower.pop();
    lower.push(point);
  }
  const upper: Position[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const point = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) upper.pop();
    upper.push(point);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}

/**
 * Side lengths (metres) of the planar minimum-area rectangle around the parcel — the basis for
 * `width_m` and `elongation`. Replicates `ST_OrientedEnvelope` (rotating calipers in degree-space)
 * with side lengths measured geodesically, since no Turf primitive computes an oriented envelope.
 */
export function orientedRectangleSidesMetres(geom: Polygon | MultiPolygon): [number, number] | null {
  const hull = convexHull(outerRingCoords(geom));
  if (hull.length < 3) return null;
  let bestArea = Infinity;
  let bestCorners: Position[] | null = null;
  for (let i = 0; i < hull.length; i++) {
    const a = hull[i];
    const b = hull[(i + 1) % hull.length];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    const ux = (b[0] - a[0]) / len;
    const uy = (b[1] - a[1]) / len;
    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;
    for (const point of hull) {
      const dx = point[0] - a[0];
      const dy = point[1] - a[1];
      const u = dx * ux + dy * uy;
      const v = -dx * uy + dy * ux;
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }
    const planarArea = (maxU - minU) * (maxV - minV);
    if (planarArea < bestArea) {
      bestArea = planarArea;
      const corner = (u: number, v: number): Position => [a[0] + u * ux - v * uy, a[1] + u * uy + v * ux];
      bestCorners = [corner(minU, minV), corner(maxU, minV), corner(maxU, maxV)];
    }
  }
  if (!bestCorners) return null;
  return [haversineMetres(bestCorners[0], bestCorners[1]), haversineMetres(bestCorners[1], bestCorners[2])];
}

interface RingMoment {
  area: number;
  cx: number;
  cy: number;
}

/**
 * Shoelace area and centroid of a single ring, computed relative to origin `o`.
 *
 * The translation is load-bearing: raw lon/lat (~123) make each `x*y` term ~5400 while a small
 * parcel's signed areas are ~1e-8, so computing in absolute coordinates loses all precision to
 * catastrophic cancellation.
 */
function ringMoment(ring: Position[], o: Position): RingMoment {
  let a2 = 0;
  let rx = 0;
  let ry = 0;
  for (let i = 0; i + 1 < ring.length; i++) {
    const x0 = ring[i][0] - o[0];
    const y0 = ring[i][1] - o[1];
    const x1 = ring[i + 1][0] - o[0];
    const y1 = ring[i + 1][1] - o[1];
    const crossProduct = x0 * y1 - x1 * y0;
    a2 += crossProduct;
    rx += (x0 + x1) * crossProduct;
    ry += (y0 + y1) * crossProduct;
  }
  return a2 === 0 ? { area: 0, cx: 0, cy: 0 } : { area: Math.abs(a2 / 2), cx: rx / (3 * a2), cy: ry / (3 * a2) };
}

/** Planar area centroid including holes and multiple parts — matches `ST_Centroid`. */
export function areaCentroid(geom: Polygon | MultiPolygon): Point | null {
  const origin = polygonParts(geom)[0][0][0];
  let cx = 0;
  let cy = 0;
  let net = 0;
  for (const rings of polygonParts(geom)) {
    for (let ringIndex = 0; ringIndex < rings.length; ringIndex++) {
      const { area, cx: rcx, cy: rcy } = ringMoment(rings[ringIndex], origin);
      const sign = ringIndex === 0 ? 1 : -1; // ring 0 is the shell; the rest are holes
      cx += sign * area * rcx;
      cy += sign * area * rcy;
      net += sign * area;
    }
  }
  if (net <= 0) return null;
  return { type: "Point", coordinates: [origin[0] + cx / net, origin[1] + cy / net] };
}

/** Flatten a street geometry to its constituent `LineString`s. */
export const toLineStrings = (geom: LineString | MultiLineString): LineString[] =>
  geom.type === "LineString" ? [geom] : geom.coordinates.map((coordinates) => ({ type: "LineString", coordinates }));
